import type {
    AggregatedTimelineData,
    AggregatedTimelineSegmentData,
    TimelineObjectType,
    TimelineSegment,
} from "@reva/shared/timelines";
import {
    type AggregatedTimelineValue,
    SEGMENT_BY_OBJECT_TYPE,
    TIMELINE_REGIONAL_LEVEL_VALUES,
    type TimelineRegionalLevel,
} from "@reva/shared/timelines";

import {OBJECT_TYPES} from "./timelineAggregation";

/**
 * Resolve segment aggregation in the aggregated timeline data.
 */
export function resolveSegment(
    aggregatedTimelineData: AggregatedTimelineData | null,
    segment: TimelineSegment | null,
): AggregatedTimelineSegmentData | null {
    if (!segment || !aggregatedTimelineData || typeof aggregatedTimelineData[segment] !== "object") return null;
    return aggregatedTimelineData[segment];
}

/**
 * Check if a timeline segment is available in the aggregated timeline data.
 */
export function hasSegment(
    aggregatedTimelineData: AggregatedTimelineData | null,
    segment: TimelineSegment | null,
): boolean {
    return resolveSegment(aggregatedTimelineData, segment) != null;
}

/**
 * Resolve regional level aggregation in the aggregated timeline data.
 */
export function resolveRegionalLevel(
    aggregatedTimelineData: AggregatedTimelineData | null,
    segment: TimelineSegment | null,
    regionalLevel: TimelineRegionalLevel | null,
): AggregatedTimelineValue[] | null {
    if (!regionalLevel) return null;
    const segmentAggregation = resolveSegment(aggregatedTimelineData, segment);
    if (
        !segmentAggregation ||
        !(regionalLevel in segmentAggregation) ||
        !Array.isArray(segmentAggregation[regionalLevel]) ||
        !segmentAggregation[regionalLevel].length
    ) {
        return null;
    }
    return segmentAggregation[regionalLevel];
}

/**
 * Check if a combination of timeline segment and regional level is available in the timeline data.
 */
export function hasRegionalLevel(
    aggregatedTimelineData: AggregatedTimelineData | null,
    segment: TimelineSegment | null,
    regionalLevel: TimelineRegionalLevel | null,
) {
    return resolveRegionalLevel(aggregatedTimelineData, segment, regionalLevel) != null;
}

/**
 * Get available regional Levels for at least one of the segments.
 */
export function getSupportedRegionalLevels(
    aggregatedTimelineData: AggregatedTimelineData | null,
    segment?: TimelineSegment | null,
    otherSegment?: TimelineSegment | null,
): TimelineRegionalLevel[] {
    return TIMELINE_REGIONAL_LEVEL_VALUES.filter(
        (regionalLevel) =>
            hasRegionalLevel(aggregatedTimelineData, segment ?? null, regionalLevel) ||
            hasRegionalLevel(aggregatedTimelineData, otherSegment ?? null, regionalLevel),
    );
}

/**
 * Get segments for "sale" or "rent" segment types.
 */
export function getTimelineSegments(objectType: TimelineObjectType): {
    sale: TimelineSegment;
    rent: TimelineSegment | null;
} {
    return SEGMENT_BY_OBJECT_TYPE[objectType];
}

/**
 * Get list of object types available in the timeline data, falling back to the first defined
 * object type when none of them have data.
 */
export function getObjectTypes(aggregatedTimelineData: AggregatedTimelineData | null): TimelineObjectType[] {
    const supported = OBJECT_TYPES.filter((o) => {
        const {sale, rent} = getTimelineSegments(o.value);
        return hasSegment(aggregatedTimelineData, sale) || hasSegment(aggregatedTimelineData, rent);
    }).map((o) => o.value);
    return supported.length ? supported : [OBJECT_TYPES[0].value];
}

/**
 * Get available regional Levels for at least one of the segments for the object type.
 */
export function getSupportedRegionalLevelsForObjectType(
    aggregatedTimelineData: AggregatedTimelineData | null,
    objectType: TimelineObjectType,
): TimelineRegionalLevel[] {
    const {sale: segmentSale, rent: segmentRent} = getTimelineSegments(objectType);
    return getSupportedRegionalLevels(aggregatedTimelineData, segmentSale, segmentRent);
}

/**
 * Sorter for Timeline row data by year (reverse).
 */
const sortByYearDesc = (a: AggregatedTimelineValue, b: AggregatedTimelineValue) => {
    if (a.year < b.year) return 1;
    if (a.year > b.year) return -1;
    return 0;
};

/**
 * Get aggregated timeline data, sorted by year descending.
 */
export function getTimelineDescending(
    aggregatedTimelineData: AggregatedTimelineData | null,
    segment: TimelineSegment | null,
    regionalLevel: TimelineRegionalLevel | null,
) {
    const timelineData = resolveRegionalLevel(aggregatedTimelineData, segment, regionalLevel);
    if (timelineData == null) return null;
    const timelineDataSorted = [...timelineData];
    timelineDataSorted.sort(sortByYearDesc);
    return timelineDataSorted;
}
